'use client';

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { usePartnershipUpgrade } from '@/api/mutations/partnershipMutation';
import { cn } from '@/lib/utils';
import { Check, Crown, Medal, Award, Loader2 } from 'lucide-react';

interface PartnershipModalProps {
  business?: any;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
}

type PartnershipTier = 'bronze' | 'silver' | 'gold';

const tiers: {
  id: PartnershipTier;
  name: string;
  price: number;
  icon: React.ComponentType<{ className?: string }>;
  color: string;
  borderColor: string;
  bgColor: string;
  iconColor: string;
  benefits: string[];
}[] = [
  {
    id: 'bronze',
    name: 'Bronze',
    price: 250,
    icon: Award,
    color: 'text-amber-700',
    borderColor: 'border-amber-600',
    bgColor: 'bg-amber-50',
    iconColor: 'text-amber-700',
    benefits: [
      'Listed as a Wee Amici Partner',
      'Placement on Partners Page',
    ],
  },
  {
    id: 'silver',
    name: 'Silver',
    price: 500,
    icon: Medal,
    color: 'text-gray-500',
    borderColor: 'border-gray-400',
    bgColor: 'bg-gray-50',
    iconColor: 'text-gray-500',
    benefits: [
      'Priority listing in search',
      'Higher listing on Partners Page',
      '4 ads per year (4 weeks each)',
      'Additional ads at $150/ad/month',
      'Monthly analytics reports',
    ],
  },
  {
    id: 'gold',
    name: 'Gold',
    price: 1000,
    icon: Crown,
    color: 'text-yellow-600',
    borderColor: 'border-yellow-500',
    bgColor: 'bg-yellow-50',
    iconColor: 'text-yellow-600',
    benefits: [
      'Top of search results',
      'Top of Partners Page',
      'Restricted to Only 4 Gold Partners per business category',
      '8 targeted ads per year (4 weeks each)',
      'Additional ads at $150/ad/month',
      'Pixel & API data for ad tracking',
      'Dedicated account manager',
    ],
  },
];

export default function PartnershipModal({
  isOpen,
  onOpenChange,
}: PartnershipModalProps) {
  const partnershipMutation = usePartnershipUpgrade();
  const [selectedTier, setSelectedTier] = useState<PartnershipTier | null>(null);

  const handleSelectTier = async () => {
    if (!selectedTier) return;

    try {
      const res = await partnershipMutation.mutateAsync({ tier: selectedTier });
      const checkoutUrl = res?.responseObject;

      if (!checkoutUrl || typeof checkoutUrl !== 'string') {
        toast.error('Could not start checkout. Please try again.');
        return;
      }

      toast.success(
        `Redirecting to checkout for the ${
          selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)
        } tier…`
      );
      onOpenChange(false);
      setSelectedTier(null);
      window.location.href = checkoutUrl;
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || 'Failed to start checkout'
      );
    }
  };

  const handleClose = (open: boolean) => {
    if (!open) {
      setSelectedTier(null);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[950px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-merriweather">
            Request Partnership
          </DialogTitle>
          <DialogDescription className="text-black">
            Select a partnership tier to unlock benefits. Qualified businesses
            only.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          {tiers.map((tier) => {
            const isSelected = selectedTier === tier.id;
            const Icon = tier.icon;

            return (
              <button
                key={tier.id}
                type="button"
                onClick={() => setSelectedTier(tier.id)}
                className={cn(
                  'relative flex flex-col rounded-xl border-2 p-5 text-left transition-all cursor-pointer hover:shadow-md',
                  isSelected
                    ? `${tier.borderColor} ${tier.bgColor} shadow-md`
                    : 'border-gray-200 bg-white hover:border-gray-300'
                )}
              >
                {isSelected && (
                  <div
                    className={cn(
                      'absolute top-3 right-3 rounded-full p-0.5',
                      tier.bgColor
                    )}
                  >
                    <Check className={cn('h-4 w-4', tier.color)} />
                  </div>
                )}

                <Icon className={cn('h-8 w-8 mb-3', tier.iconColor)} />

                <h3
                  className={cn(
                    'text-lg font-bold font-merriweather',
                    tier.color
                  )}
                >
                  {tier.name}
                </h3>

                <p className="text-2xl font-bold mt-1 text-black">
                  ${tier.price.toLocaleString()}
                  <span className="text-sm font-normal text-gray-500">
                    /year
                  </span>
                </p>

                <ul className="mt-4 space-y-2 flex-1">
                  {tier.benefits.map((benefit) => (
                    <li
                      key={benefit}
                      className="flex items-start gap-2 text-sm text-gray-700"
                    >
                      <Check className="h-4 w-4 text-green-600 mt-0.5 shrink-0" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>
              </button>
            );
          })}
        </div>

        <div className="flex justify-end gap-3 mt-4">
          <Button
            variant="outline"
            className="rounded-full"
            onClick={() => handleClose(false)}
            disabled={partnershipMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            className="rounded-full border-[#cdc4c1]"
            onClick={handleSelectTier}
            disabled={!selectedTier || partnershipMutation.isPending}
          >
            {partnershipMutation.isPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing...
              </>
            ) : selectedTier ? (
              `Continue with ${selectedTier.charAt(0).toUpperCase() + selectedTier.slice(1)} — $${tiers.find((t) => t.id === selectedTier)?.price.toLocaleString()}`
            ) : (
              'Select a tier'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
